// Sharing a conversation by link (ADR-0084): the header button, the share
// modal and the settings section. One list component serves both lists.
// Every text from the server — titles above all — goes in by textContent.

import { axios } from './vendor.js';
import {
    EMPTY_ACCOUNT_LIST,
    EMPTY_CONVERSATION_LIST,
    REVOKE_CONFIRMATION,
    shareLine
} from './share_wording.js';

const GENERIC_ERROR = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';

let targetId = null;

// Called by chat.js wherever it sets the open conversation (D-5). A no-op
// where the button does not exist (the guest chat).
export function setShareTarget(conversationId) {
    targetId = conversationId || null;
    const button = document.getElementById('shareConversationBtn');
    if (button) button.classList.toggle('d-none', !targetId);
}

function sharesUrl(conversationId) {
    return `/api/conversations/${encodeURIComponent(conversationId)}/shares`;
}

function errorText(error) {
    return error?.response?.data?.error || GENERIC_ERROR;
}

// The fallback for a browser that refuses the clipboard API.
function legacyCopy(text) {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    area.remove();
    return copied;
}

async function copyUrl(url, button, input) {
    let copied = false;
    try {
        await navigator.clipboard.writeText(url);
        copied = true;
    } catch (e) {
        copied = legacyCopy(url);
    }
    if (!copied) {
        input?.select();
        return;
    }
    button.textContent = 'Kopiert';
    setTimeout(() => { button.textContent = 'Kopieren'; }, 2000);
}

function smallButton(label, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `btn btn-sm ${className}`;
    button.textContent = label;
    return button;
}

function emptyItem(text) {
    const item = document.createElement('li');
    item.className = 'share-list__empty text-muted small';
    item.textContent = text;
    return item;
}

async function revokeShare(share, item, container, options) {
    if (!confirm(REVOKE_CONFIRMATION)) return;
    try {
        await axios.delete(`/api/shares/${encodeURIComponent(share.token)}`);
    } catch (error) {
        // Already gone is the outcome the owner asked for.
        if (error?.response?.status !== 404) {
            window.alert(errorText(error));
            return;
        }
    }
    item.remove();
    if (!container.querySelector('.share-list__item')) {
        container.appendChild(emptyItem(options.emptyText));
    }
}

function shareItem(share, container, options) {
    const item = document.createElement('li');
    item.className = 'share-list__item';
    if (options.showTitle) {
        const title = document.createElement('div');
        title.className = 'share-list__title';
        title.textContent = share.conversation_title;
        item.appendChild(title);
    }
    const line = document.createElement('div');
    line.className = 'share-list__line small text-muted';
    line.textContent = shareLine(share);
    const copy = smallButton('Kopieren', 'btn-shepwise-outline');
    copy.addEventListener('click', () => copyUrl(share.url, copy, null));
    const revoke = smallButton('Widerrufen', 'btn-outline-danger');
    revoke.addEventListener('click', () => revokeShare(share, item, container, options));
    const actions = document.createElement('div');
    actions.className = 'share-list__actions';
    actions.append(copy, revoke);
    item.append(line, actions);
    return item;
}

export function renderShareList(container, shares, options = {}) {
    container.replaceChildren();
    if (!shares.length) {
        container.appendChild(emptyItem(options.emptyText));
        return;
    }
    shares.forEach(share => container.appendChild(shareItem(share, container, options)));
}

// --- the modal ---------------------------------------------------------------

function modalPart(id) {
    return document.getElementById(id);
}

function showAlert(text) {
    const alert = modalPart('shareAlert');
    alert.textContent = text || '';
    alert.classList.toggle('d-none', !text);
}

function showCreatePart(body) {
    modalPart('shareCreatePart').classList.toggle('d-none', !body.can_create);
    modalPart('shareBlockedPro').classList.toggle('d-none', body.blocked_by !== 'pro');
    modalPart('shareBlockedSwitch').classList.toggle('d-none', body.blocked_by !== 'switch');
    modalPart('shareContextNote').classList.toggle('d-none', !body.has_context_blocks);
    modalPart('shareResult').classList.add('d-none');
}

function renderModalList(shares) {
    renderShareList(modalPart('shareList'), shares, { emptyText: EMPTY_CONVERSATION_LIST });
}

async function loadModal() {
    showAlert(null);
    if (!targetId) return;
    try {
        const response = await axios.get(sharesUrl(targetId));
        showCreatePart(response.data);
        renderModalList(response.data.shares);
    } catch (error) {
        showAlert(errorText(error));
    }
}

function checkedLifetime() {
    const checked = document.querySelector('input[name="shareLifetime"]:checked');
    return checked ? checked.value : '7d';
}

async function createShare(button) {
    showAlert(null);
    button.disabled = true;
    try {
        const created = await axios.post(sharesUrl(targetId), { lifetime: checkedLifetime() });
        modalPart('shareResultUrl').value = created.data.share.url;
        modalPart('shareResult').classList.remove('d-none');
        const response = await axios.get(sharesUrl(targetId));
        renderModalList(response.data.shares);
    } catch (error) {
        showAlert(errorText(error));
    } finally {
        button.disabled = false;
    }
}

export function initShareModal() {
    const modal = modalPart('shareModal');
    modal.addEventListener('show.bs.modal', loadModal);
    const create = modalPart('shareCreateBtn');
    create.addEventListener('click', () => createShare(create));
    const copy = modalPart('shareResultCopy');
    const input = modalPart('shareResultUrl');
    copy.addEventListener('click', () => copyUrl(input.value, copy, input));
}

// --- the settings section ----------------------------------------------------

export function initSettingsShares() {
    const container = document.getElementById('settingsShareList');
    const modal = document.getElementById('settingsModal');
    if (!modal) return;
    modal.addEventListener('show.bs.modal', async () => {
        try {
            const response = await axios.get('/api/shares');
            renderShareList(container, response.data.shares, {
                showTitle: true,
                emptyText: EMPTY_ACCOUNT_LIST
            });
        } catch (error) {
            container.replaceChildren(emptyItem(errorText(error)));
        }
    });
}
